import React from 'react';
import { Star, Quote, ExternalLink } from 'lucide-react';
import airbnbLogo from '../assets/airbnb-logo.png';

const Reviews = ({ property }) => {
  const displayedReviews = property.reviews.slice(0, 4);

  return (
    <div className="bg-background py-10 md:py-16 lg:py-20">
      <div className="max-w-[1120px] mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4 md:mb-6" style={{fontFamily: 'Cormorant Garamond, Georgia, serif'}}>
            What Our Guests Say
          </h2>
          
          {/* Airbnb Credibility Badge */}
          <div className="inline-flex flex-col items-center gap-3 md:gap-4 bg-card border-2 border-border rounded-xl md:rounded-2xl px-4 md:px-8 py-4 md:py-6 mb-4 md:mb-6 shadow-sm">
            {/* Airbnb Logo */}
            <div className="flex items-center gap-2 md:gap-3">
              <img src={airbnbLogo} alt="Airbnb" className="h-6 md:h-8 w-auto" />
              <div className="h-4 md:h-6 w-px bg-border"></div>
              <span className="text-[10px] md:text-sm font-medium text-muted-foreground uppercase tracking-wider">Verified Guest Reviews</span>
            </div>
            
            {/* Prominent Rating */}
            <div className="flex items-center gap-2 md:gap-3">
              <div className="flex items-center gap-0.5 md:gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 md:w-6 md:h-6 fill-[#FF5A5F] text-[#FF5A5F]" />
                ))}
              </div>
              <span className="text-2xl md:text-4xl font-bold text-accent" style={{fontFamily: 'Cormorant Garamond, Georgia, serif'}}>
                {property.rating}
              </span>
              <span className="text-muted-foreground font-medium text-xs md:text-base">
                ({property.reviewCount} reviews)
              </span>
            </div>
            
            {/* Guest Favourite Badge */}
            {property.isGuestFavourite && (
              <div className="flex items-center gap-1.5 md:gap-2 bg-[#FF5A5F]/10 border border-[#FF5A5F]/30 rounded-full px-3 md:px-4 py-1.5 md:py-2">
                <Star className="w-3 h-3 md:w-4 md:h-4 fill-[#FF5A5F] text-[#FF5A5F]" />
                <span className="text-[10px] md:text-sm font-medium text-[#FF5A5F]">Guest Favourite · Top 5% of homes</span>
              </div>
            )}
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
          {displayedReviews.map((review) => (
            <div 
              key={review.id} 
              className="bg-card border-2 border-border rounded-xl md:rounded-2xl p-4 md:p-6 lg:p-8 hover:shadow-lg hover:border-primary/30 transition-all relative"
            >
              {/* Quote icon */}
              <Quote className="absolute top-3 right-3 md:top-4 md:right-4 w-6 h-6 md:w-8 md:h-8 text-primary/20" />
              
              {/* Review content */}
              <p className="text-muted-foreground leading-relaxed mb-4 md:mb-6 text-xs md:text-sm lg:text-base line-clamp-4">
                "{review.comment}"
              </p>
              
              {/* Reviewer info */}
              <div className="flex items-center gap-3 md:gap-4">
                <img
                  src={review.userImage}
                  alt={review.userName}
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-primary/30 object-cover"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-accent text-sm md:text-base truncate" style={{fontFamily: 'Cormorant Garamond, Georgia, serif'}}>
                    {review.userName}
                  </div>
                  <div className="text-[10px] md:text-xs text-muted-foreground">
                    {review.date}
                  </div>
                </div>
                <div className="flex items-center gap-0.5 md:gap-1 flex-shrink-0">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-2.5 h-2.5 md:w-3 md:h-3 fill-primary text-primary" />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* See all reviews on Airbnb link */}
        <div className="text-center mt-6 md:mt-10">
          <a 
            href="https://www.airbnb.co.uk/users/profile/1462898354019480359?previous_page_name=PdpHomeMarketplace"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#FF5A5F] hover:text-[#E04E53] font-medium transition-colors text-sm md:text-base"
            data-testid="airbnb-reviews-link"
            onClick={() => { if (window.va) window.va('event', { name: 'airbnb_click', data: { location: 'reviews_profile' } }); }}
          >
            <img src={airbnbLogo} alt="Airbnb" className="h-4 md:h-5 w-auto" />
            <span>See all reviews on Airbnb</span>
            <ExternalLink className="w-3.5 h-3.5 md:w-4 md:h-4" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default Reviews;